import "./base/gsap";
import barba from "@barba/core";
import ScrollTrigger from "gsap/ScrollTrigger";

import { runDefault, runLayoutDefault, runComponentDefault, runModuleDefault } from "./utils/runner-functions";

import { initVariables, isTouchDevice } from "./utils/variables";
import { initScroller, requestScrollTriggerRefresh } from "./base/scroll";
import { runLoader, skipLoader, loaderDone } from "./base/loader";
import { initBarba, setPageInitCallback } from "./base/barba";
import initWebGL, { getWebGLInstance } from "./components/webgl";
import initLogo, { updateLogoPage } from "./components/logo";
import { refreshWorkListing } from "./utils/webgl/WorkListing";
import { updateRootPageCookie } from "./layout/close-navigation";
import initSplitText from "./base/split-text";
import playLogoIntro, { markIntroDone } from "./components/logo-intro";

async function init() {
    const initialContainer = document.querySelector('[data-barba="container"]') as HTMLElement;
    const isHome = initialContainer.dataset.barbaNamespace === "home";

    let introPromise: Promise<void> | null = null;
    let introGate: Promise<void> | undefined;
    let markPageScriptsReady!: () => void;
    const pageScriptsReady = new Promise<void>(resolve => (markPageScriptsReady = resolve));

    if (!isHome) {
        skipLoader();

        let markIntroGate!: () => void;
        introGate = new Promise<void>(resolve => (markIntroGate = resolve));

        introPromise = playLogoIntro(initialContainer, () => pageScriptsReady, markIntroGate);
    }

    initGlobalScripts();
    initGlobalLayoutScripts();
    initGlobalComponentScripts();
    await initPageScripts();
    markPageScriptsReady();

    if (isHome) {
        initSplitText(initialContainer, 0.3, loaderDone);
    } else {
        initSplitText(initialContainer);
    }
    initWebGL(initialContainer, 0.1, introGate);
    initLogo(initialContainer);
    barba.hooks.beforeEnter(data => updateLogoPage(data.next.container as HTMLElement));

    if (isHome) {
        await runLoader();
        markIntroDone();
    } else {
        await introPromise;
    }

    ScrollTrigger.refresh();

    requestAnimationFrame(() => requestScrollTriggerRefresh(250));

    setPageInitCallback(initPageScripts);
    initBarba();
}

function initGlobalScripts() {
    initVariables();
    initScroller();
    runDefault("grid");
    initArchiveListingSync();
}

function initArchiveListingSync() {
    document.addEventListener("library:tracks-updated", () => {
        runModuleDefault("library-waveform", ".library__tracks");
        runModuleDefault("library-hover", ".library__tracks");
        void import("./modules/library-player").then(mod => mod.rebindLibraryPlayerRows());
    });

    document.addEventListener("work:listing-updated", event => {
        const { site } = (event as CustomEvent<{ site?: string }>).detail ?? {};
        const listing = document.querySelector<HTMLElement>(`[data-listing="work"][data-site="${site}"]`);
        if (!listing) return;

        const container = document.querySelector<HTMLElement>('[data-barba="container"]');
        const instance = container ? getWebGLInstance(container) : undefined;
        if (!instance) return;

        refreshWorkListing(instance.planePool, listing);
    });
}

function initGlobalLayoutScripts() {
    runLayoutDefault("menu");

    if (!isTouchDevice) {
        runLayoutDefault("saver");
    }
}

function initGlobalComponentScripts() {
    if (!isTouchDevice) {
        runComponentDefault("cursor");
    }
}

async function initPageScripts() {
    updateRootPageCookie();

    runDefault("link-marquee");

    runLayoutDefault("clients");

    if (!isTouchDevice) {
        runLayoutDefault("header-work-video");
        runLayoutDefault("close-navigation");
    }

    runComponentDefault("btn-hover", ".btn-hover");
    runComponentDefault("video-player", ".video_wrap");
    runComponentDefault("video-player-mix", ".video_wrap[data-video-site='music'] .video_mix");
    runComponentDefault("bpm-range", ".bpm-range");

    if (!isTouchDevice) {
        runComponentDefault("video-card", ".video-card");
    }

    runModuleDefault("hero", ".hero");
    runModuleDefault("about", ".about");
    runModuleDefault("related", ".related");
    runModuleDefault("services", ".services");
    runModuleDefault("sticker-ring", ".sticker-ring");
    runModuleDefault("library-hover", ".library__tracks");
    runModuleDefault("library-waveform", ".library__tracks");
    runModuleDefault("library-filters", "[data-library-filters]");
    runModuleDefault("work-filters", "[data-work-filters]");
    runModuleDefault("library-player", "#library-audio-player");
}

function safeInit() {
    requestAnimationFrame(() => setTimeout(init, 1));
}

if (!(document.readyState === "loading")) safeInit();
else document.addEventListener("DOMContentLoaded", safeInit);
